<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <div v-if="isMobile" class="mt-1" />
        <grid
            ref="grid"
            :column-defs="columnDefs"
            :row-data="rowData"
            :is-auto-width="true"
            :is-search="false"
            :statusBar="statusBar"
            grid-theme="ag-theme-alpine-report"
            class="door-codes-log-report"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            />
            <responsive-button
                :href="exportExcelHref"
                :icon="['fas', 'file-excel']"
                @click="generatingExcelReportSuccessToast"
                variant="primary"
            />
            <exportToPDFButton
                :href="exportPdfHref"
                :pdfCacheKey="pdfCacheKey"
                :pageOrientation="pageOrientation.toLowerCase()"
            />
            <responsive-dropdown
                :icon="['fas', 'file']"
                :items="pageOrientationOptions"
                variant="primary"
            >
                {{ pageOrientation }}
            </responsive-dropdown>
            <report-settings-modal
                :modalId="reportSettingsModalId"
                :initialValues="reportSettings"
            />
        </grid>
    </page>
</template>

<script>
    import { mapGetters } from 'vuex';
    import format from 'date-fns/format';
    import API from '../../api';
    import Page from '../generic/page/page.vue';
    import Grid from '../generic/grid/grid.vue';
    import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
    import ResponsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
    import DateRangePicker, {
        nameToRange,
        TODAY,
        YESTERDAY,
        THIS_MONTH,
        LAST_MONTH,
        ALL_DATES,
        TOMORROW,
        THIS_WEEK,
        NEXT_WEEK,
        NEXT_MONTH,
        THIS_YEAR,
        LAST_WEEK,
    } from '../generic/date-range-picker/date-range-picker.vue';
    import { REPORT_DOOR_CODES_LOG_ENDPOINT } from '../../endpoints';
    import { PAGE_ORIENTATION_LABELS } from '../../constants'
    import ReportSummaryBar from './report-summary-bar.vue';
    import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
    import ReportSettingsModal, {
        DATE_PERIODS_INPUT,
    } from './report-settings/report-settings-modal.vue';
import { defineAsyncComponent } from 'vue';

    const exportToPDFButton = defineAsyncComponent(() =>
        import(
            /* webpackChunkName: "lx-export-to-pdf-button" */
            '../generic/export-to-pdf-button/export-to-pdf-button.vue'
        ))

    export default {
        name: 'DoorCodesLog',
        components: {
            Page,
            Grid,
            ResponsiveButton,
            ResponsiveDropdown,
            DateRangePicker,
            reportSummaryBar: ReportSummaryBar,
            exportToPDFButton,
            ReportSettingsModal
        },
        mixins: [removeScrollMixin],
        data() {
            return {
                title: 'Door Codes Log',
                tabs: [
                    {
                        title: 'Door Codes Log',
                        route: '/app/reports/door-codes-log/',
                        active: true,
                    },
                ],
                dateRange: nameToRange(THIS_MONTH),
                pageOrientation: PAGE_ORIENTATION_LABELS[0],
                predefinedRanges: [
                    YESTERDAY,
                    TODAY,
                    TOMORROW,
                    LAST_WEEK,
                    THIS_WEEK,
                    NEXT_WEEK,
                    LAST_MONTH,
                    THIS_MONTH,
                    NEXT_MONTH,
                    THIS_YEAR,
                    ALL_DATES,
                ],
                dateRequestFormat: 'yyyy-MM-dd',
                busy: false,
                gridApi: null,
                rowData: [],
                windowWidth: 0,
                windowHeight: 0,
                statusBar: null,
                pdfCacheKey: null,
                reportSettingsModalId: 'report-settings-modal',
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
            columnDefs() {
                return [
                    {
                        headerName: '#',
                        valueGetter: (params) => params.node.rowIndex + 1,
                        minWidth: 40,
                    },
                    {
                        field: 'reservation',
                        headerName: 'Reservation',
                        minWidth: 100,
                        cellRenderer: ({ data }) => {
                            const { reservation } = data
                            return reservation === 'Cancelled'
                                ? reservation
                                : `<a target="_self" href="/app/reservation/edit/invoice/${reservation}">${reservation}</a>`;
                        }
                    },
                    {
                        field: 'property',
                        headerName: 'Property',
                        minWidth: 100,
                    },
                    {
                        field: 'lock',
                        headerName: 'Lock',
                        minWidth: 100,
                    },
                    {
                        field: 'guest_name',
                        headerName: 'Guest Name',
                        minWidth: 100,
                    },
                    {
                        field: 'email',
                        headerName: 'Email',
                        minWidth: 100,
                    },
                    {
                        field: 'access_code',
                        headerName: 'Access Code',
                        minWidth: 100,
                    },
                    {
                        field: 'start_access_time',
                        headerName: 'Start Access',
                        type: 'dateTimeColumn',
                        minWidth: 100,
                    },
                    {
                        field: 'end_access_time',
                        headerName: 'End Access',
                        type: 'dateTimeColumn',
                        minWidth: 100,
                    },
                    {
                        field: 'created_at',
                        headerName: 'Created',
                        type: 'dateTimeColumn',
                        minWidth: 100,
                    },
                    {
                        field: 'updated_at',
                        headerName: 'Updated',
                        type: 'dateTimeColumn',
                        minWidth: 100,
                    },
                    {
                        field: 'cancelled',
                        headerName: 'Cancelled',
                        minWidth: 100,
                        autoWidthExclude: true
                    },
                ]
            },
            exportPdfHref() {
                let url = '/app/reports/door-codes-log/row-data/pdf/';
                url += `?from_date=${this.startDateRequest}`;
                url += `&to_date=${this.endDateRequest}`;
                return url;
            },
            exportExcelHref() {
                let url = '/app/reports/door-codes-log/row-data/excel/';
                url += `?from_date=${this.startDateRequest}`;
                url += `&to_date=${this.endDateRequest}`;
                return url;
            },
            startDateRequest() {
                return format(this.dateRange.from, this.dateRequestFormat);
            },
            endDateRequest() {
                return format(this.dateRange.to, this.dateRequestFormat);
            },
            pageOrientationOptions() {
                return [
                    {
                        label: PAGE_ORIENTATION_LABELS[0],
                        click: () => { this.pageOrientation = PAGE_ORIENTATION_LABELS[0] },
                    },
                    {
                        label: PAGE_ORIENTATION_LABELS[1],
                        click: () => { this.pageOrientation = PAGE_ORIENTATION_LABELS[1] },
                    },
                ]
            },
            isMobile() {
                const md = 720;
                return this.windowWidth < md || this.windowHeight < md;
            },
            reportSettings() {
                return {
                    name: 'Door Codes Log',
                    report_sysname: 'DOOR_CODES_LOG',
                    settings: [
                        {
                            type: DATE_PERIODS_INPUT,
                            value: 'this_month',
                            predefinedRanges: this.predefinedRanges,
                            label: 'Period',
                            name: 'from_date',
                        },
                    ],
                };
            },
        },
        mounted() {
            this.fetchData();
            const grid = this.$refs.grid;
            this.gridApi = grid.gridOptions.api;
            this.onResize();
            window.addEventListener('resize', this.onResize);
        },
        beforeUnmount() {
            window.removeEventListener('resize', this.onResize);
        },
        beforeMount() {
            this.statusBar = {
                statusPanels: [
                    {
                        key: 'reportSummaryBar',
                        statusPanel: 'reportSummaryBar',
                        align: 'left',
                    },
                ],
            };
        },
        methods: {
            async fetchData() {
                try {
                    this.busy = true;
                    const { data } = await API.get(
                        REPORT_DOOR_CODES_LOG_ENDPOINT(),
                        {
                            params: {
                                from_date: this.startDateRequest,
                                to_date: this.endDateRequest,
                            },
                        }
                    );
                    this.pdfCacheKey = data.pdf_cache_key
                    this.rowData = [...data.items]
                } catch (e) {
                    this.errorToast({ text: "Could not fetch data" });
                } finally {
                    this.busy = false
                }
            },
            onResize() {
                this.windowWidth = window.innerWidth;
                this.windowHeight = window.innerHeight;
                const statusBarComponent = this.gridApi.getStatusPanel('reportSummaryBar');
                let componentInstance = statusBarComponent;
                if (statusBarComponent.getFrameworkComponentInstance) {
                    componentInstance = statusBarComponent.getFrameworkComponentInstance();
                }
                componentInstance.setVisible(this.isMobile);
                componentInstance.setTitle(this.title);
            },
            handleDaterangeChange(value) {
                this.dateRange = { ...value };
                this.fetchData();
            },
        }
    };
</script>